import {
  QueryGroup,
  OperationList,
  OperationFilter,
  CustomFilter,
  Assigned,
} from '../../services/types/Operation';
import { ApiState, Action } from '../../types';
import { serviceReducer, callService } from '../../shared/hooks/serviceReducer';
import Role from '../../types/role';
import { getDefaultQueryGroup, getDefaultSubAssigned } from './functions';
import { CustomFilterForm } from '../../services/types/CustomFilter';

type getColumnsType = (filter: OperationFilter) => string[];
type setFilterType = (filter: OperationFilter) => void;

export type OperAction =
  | Action<OperationList, OperationFilter>
  | {
      type: 'ASSIGNED';
      value: Assigned;
      role: Role;
      setFilter: setFilterType;
      getHideColumns: getColumnsType;
      getRemoveColumns: getColumnsType;
    }
  | {
      type: 'QUERY_GROUP';
      value: QueryGroup;
      role: Role;
      setFilter: setFilterType;
      getHideColumns: getColumnsType;
      getRemoveColumns: getColumnsType;
    }
  | {
      type: 'SUBQUERY_ASSIGNED';
      value: string | null;
      id: number | null;
      setFilter: setFilterType;
      getHideColumns: getColumnsType;
      getRemoveColumns: getColumnsType;
      customFilters: CustomFilterForm | null;
      customFilterId?: number | null;
    }
  | {
      type: 'SET_SUBQUERY';
      value: string | null;
      id: number | null;
      setFilter: setFilterType;
    }
  | {
      type: 'ADD_UNSAVED_CUSTOM_FILTER';
      setFilter: setFilterType;
      customFilters: CustomFilterForm;
    }
  | {
      type: 'DELETE_UNSAVED_CUSTOM_FILTER';
      setFilter: setFilterType;
    }
  | {
      type: 'ADD_NEW_CUSTOM_FILTER';
      newCustomFilter: CustomFilter;
    }
  | {
      type: 'DELETE_CUSTOM_FILTER';
      customFilterId: number;
      setFilter: setFilterType;
    }
  | {
      type: 'CLEAN_APPLIED_CUSTOM_FILTER';
      setFilter: setFilterType;
    }
  | { type: 'PAGE'; value: number; setFilter: setFilterType }
  | { type: 'PAGE_SIZE'; value: number; setFilter: setFilterType }
  | { type: 'ORDER'; column: string; descending: boolean; setFilter: setFilterType }
  | {
      type: 'HIDE_COLUMN';
      column: string;
      checked: boolean;
      setFilter: setFilterType;
      setColumns: setFilterType;
    };

const reducer = (
  state: ApiState<OperationList, OperationFilter>,
  action: OperAction
): ApiState<OperationList, OperationFilter> => {
  switch (action.type) {
    case 'ASSIGNED': {
      const queryGroup = getDefaultQueryGroup(action.role, action.value);
      const subqueryType = getDefaultSubAssigned(action.role, action.value, queryGroup);
      const filter = {
        ...state.filter,
        assigned: action.value,
        queryGroup,
        subqueryType,
        subqueryId: null,
        pageNumber: 1,
      };
      filter.hideColumns = action.getHideColumns(filter);
      filter.removeColumns = action.getRemoveColumns(filter);
      return callService(state, filter, action.setFilter);
    }
    case 'QUERY_GROUP': {
      const subqueryType = getDefaultSubAssigned(action.role, state.filter.assigned, action.value);
      const filter = { ...state.filter, queryGroup: action.value, subqueryType, pageNumber: 1 };
      filter.hideColumns = action.getHideColumns(filter);
      filter.removeColumns = action.getRemoveColumns(filter);
      return callService(state, filter, action.setFilter);
    }
    case 'SUBQUERY_ASSIGNED': {
      const filter = {
        ...state.filter,
        subqueryType: action.value,
        subqueryId: action.id,
        pageNumber: 1,
      };

      const hiddenColumns = action.getHideColumns(filter);
      // only update hidden columns if the configuration is not an empty array
      if (hiddenColumns.length) {
        filter.hideColumns = action.getHideColumns(filter);
      }

      const removeColumns = action.getRemoveColumns(filter);
      // only update remove columns if the configuration is not an empty array
      if (removeColumns.length) {
        filter.removeColumns = action.getRemoveColumns(filter);
      }

      if (action.customFilters) {
        filter.customFilters = action.customFilters;
      }

      if (action.customFilterId) {
        filter.customFilterId = action.customFilterId;
      }

      return callService(state, filter, action.setFilter);
    }
    case 'SET_SUBQUERY': {
      const filter = {
        ...state.filter,
        subqueryType: action.value,
        subqueryId: action.id,
      };
      action.setFilter(filter);
      return { ...state, filter };
    }
    case 'ADD_UNSAVED_CUSTOM_FILTER': {
      const filter = {
        ...state.filter,
        customFilters: action.customFilters,
        customFilterId: 0,
      };
      action.setFilter(filter);
      return { ...state, filter };
    }
    case 'DELETE_UNSAVED_CUSTOM_FILTER': {
      const filter = {
        ...state.filter,
        subqueryId: null,
        customFilters: null,
        customFilterId: 0,
      };
      action.setFilter(filter);
      return { ...state, filter };
    }
    case 'ADD_NEW_CUSTOM_FILTER': {
      const newData = state.data
        ? {
            ...state.data,
            customFilters: [...state.data.customFilters, { ...action.newCustomFilter }],
          }
        : null;

      return {
        ...state,
        data: newData,
      };
    }
    case 'DELETE_CUSTOM_FILTER': {
      const newData = state.data
        ? {
            ...state.data,
            customFilters: [
              ...state.data.customFilters.filter((c) => c.id !== action.customFilterId),
            ],
          }
        : null;
      const filter = {
        ...state.filter,
        subqueryType: null,
        subqueryId: null,
        customFilters: null,
        customFilterId: 0,
      };
      action.setFilter(filter);

      return {
        ...state,
        filter,
        data: newData,
      };
    }
    case 'CLEAN_APPLIED_CUSTOM_FILTER': {
      const filter = {
        ...state.filter,
        subqueryType: null,
        subqueryId: null,
        customFilters: null,
        customFilterId: 0,
      };
      action.setFilter(filter);

      return {
        ...state,
        filter,
      };
    }
    case 'PAGE':
      return callService(state, { pageNumber: action.value }, action.setFilter);
    case 'PAGE_SIZE':
      return callService(state, { pageSize: action.value }, action.setFilter);
    case 'ORDER':
      return callService(
        state,
        { sortField: action.column, sortDescending: action.descending },
        action.setFilter
      );
    case 'HIDE_COLUMN': {
      const newList = action.checked
        ? [...state.filter.hideColumns].filter((x) => x !== action.column)
        : [...state.filter.hideColumns, action.column];
      newList.sort();
      const filter = { ...state.filter, hideColumns: newList };
      action.setColumns(filter);
      action.setFilter(filter);
      return { ...state, filter };
    }
    default:
      return serviceReducer(state, action);
  }
};

export default reducer;
